import { FilterIcon } from "@xola/icons";
import { Alert } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import React, { useEffect, useMemo } from "react";
import SimpleBar from "simplebar-react";
import { Purchase, PurchaseItem, PurchaseStatus } from "types";
import { InfiniteList } from "../Common/InfiniteList";
import { ErrorBoundary } from "../ErrorBoundary";
import { Loader } from "../Loader";
import { PurchaseItemDetails } from "./PurchaseItemDetails";
import { PurchaseListItem } from "./PurchaseListItem";
import { useHasBaseCollectionData } from "@/hooks/useInfiniteCollection";
import { useIsInViewport } from "@/hooks/useIsInViewport";
import { usePurchaseFilterStore } from "@/stores/purchaseFilterStore";

interface PurchaseListLayoutProps {
    purchases: Purchase[];
    isLoadingPurchases: boolean;
    purchase?: Purchase;
    item?: PurchaseItem;
    hasNextPage?: boolean;
    fetchNextPage: () => void;
    height: string;
    isAdvancedSearch: boolean;
    isFetchingNextPage: boolean;
}

export const PurchaseListLayout = ({
    purchases = [],
    isLoadingPurchases,
    purchase: selectedPurchase,
    hasNextPage = false,
    fetchNextPage,
    item: selectedItem,
    height,
    isAdvancedSearch,
    isFetchingNextPage,
}: PurchaseListLayoutProps) => {
    const resetToDefaults = usePurchaseFilterStore((state) => state.resetToDefaults);

    const hasBasePurchaseData = useHasBaseCollectionData("purchases", {
        status: PurchaseStatus.COMMITTED,
        limit: 20,
    });

    return (
        <div className="flex h-full lg:flex-row">
            <div className="flex w-50 flex-shrink-0 flex-col border-r border-gray-light print:!hidden lg:w-64 xl:w-75">
                <SimpleBar style={{ height }}>
                    {/* Left-hand side section that shows list of purchases */}
                    {purchases.length > 0 && (!isLoadingPurchases || isFetchingNextPage) ? (
                        <InfiniteList
                            className="divide-y divide-gray-light"
                            isLoading={isLoadingPurchases || isFetchingNextPage}
                            hasMore={hasNextPage}
                            onLoadMore={fetchNextPage}
                        >
                            <PurchaseList
                                purchases={purchases}
                                selectedPurchase={selectedPurchase}
                                selectedItem={selectedItem}
                            />
                        </InfiniteList>
                    ) : (
                        <div className="m-4">
                            <NoBookingsMessage
                                purchases={purchases}
                                isLoadingPurchases={isLoadingPurchases}
                                isFetchingNextPage={isFetchingNextPage}
                            />
                        </div>
                    )}
                </SimpleBar>
            </div>

            {purchases.length === 0 && isLoadingPurchases && (
                <div className="flex w-full flex-col items-center pt-8 align-middle">
                    <Loader size="tiny" className="!p-0 !text-left text-gray-darker" text="Fetching Purchases" />
                </div>
            )}

            {purchases.length === 0 && !isLoadingPurchases && (
                <div className="flex w-full flex-col items-center pt-8 align-middle">
                    <span className="text-lg font-bold">{hasBasePurchaseData ? "No Results" : "No Purchases"}</span>
                    {isAdvancedSearch && (
                        <SecondaryButton size="small" className="mt-6 items-center" onClick={resetToDefaults}>
                            <FilterIcon className="mr-1" /> Reset Search
                        </SecondaryButton>
                    )}
                </div>
            )}

            {purchases.length > 0 && selectedPurchase && (
                <PurchaseItemDetails purchase={selectedPurchase} item={selectedItem} />
            )}
        </div>
    );
};

const NoBookingsMessage = ({
    purchases = [],
    isLoadingPurchases,
    isFetchingNextPage,
}: {
    purchases: Purchase[];
    isLoadingPurchases: boolean;
    isFetchingNextPage: boolean;
}) => {
    const hasBasePurchaseData = useHasBaseCollectionData("purchases", {
        status: PurchaseStatus.COMMITTED,
        limit: 20,
    });
    if (purchases.length > 0 && isFetchingNextPage && !isLoadingPurchases) {
        return null;
    }

    return (
        <Alert color="secondary" data-testid="purchases-list-layout-no-bookings-message">
            {isLoadingPurchases ? (
                <Loader size="tiny" className="!p-0 !text-left text-gray-darker" text="Fetching Purchases" />
            ) : (
                <span className="text-base">{hasBasePurchaseData ? "No Results Found" : "No Purchases"}</span>
            )}
        </Alert>
    );
};

interface PurchaseListProps {
    purchases: Purchase[];
    selectedPurchase?: Purchase;
    selectedItem?: PurchaseItem;
}

const PurchaseList = ({ purchases, selectedPurchase, selectedItem }: PurchaseListProps) => {
    const itemReferences: { [key: string]: HTMLSpanElement | null } = useMemo(() => ({}), []);
    const isInViewport = useIsInViewport(itemReferences?.[selectedPurchase?.id ?? ""]);

    useEffect(() => {
        const itemReference = selectedPurchase && itemReferences?.[selectedPurchase?.id];

        // scroll only if item is not in the viewport
        if (itemReference && !isInViewport) {
            itemReference.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
        // not adding isInViewport by purpose to avoid auto scroll back when the user is scrolling
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPurchase, itemReferences]);

    return (
        <div className="divide-y divide-gray-light">
            {purchases.map((purchase: Purchase) => {
                if (!purchase.items) {
                    return null;
                }
                return (
                    <ErrorBoundary
                        key={purchase.id}
                        fallbackMessage={`Error displaying purchase for ${purchase.customerName}`}
                    >
                        <span
                            ref={(element) => {
                                itemReferences[purchase.id] = element;
                            }}
                        >
                            <PurchaseListItem
                                purchase={purchase}
                                isActive={purchase.id === selectedPurchase?.id}
                                selectedItem={selectedItem}
                            />
                        </span>
                    </ErrorBoundary>
                );
            })}
        </div>
    );
};
